import { useAutoScroll } from '@/hooks/use-auto-scroll';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { RuntimeEvent } from '@/lib/agent-types';

type EventLogProps = {
  events: RuntimeEvent[];
  onClear: () => void;
};

// ── Helpers ─────────────────────────────────────────────────────

const EVENT_COLORS: Record<string, string> = {
  text: 'text-muted-foreground',
  tool_use: 'text-blue-500',
  tool_result: 'text-green-500',
  done: 'text-muted-foreground',
  error: 'text-red-500',
};

function truncateJson(obj: unknown, maxLen = 100): string {
  const str = JSON.stringify(obj);
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + '...';
}

// ── Event Row ───────────────────────────────────────────────────

function EventRow({ event, index }: { event: RuntimeEvent; index: number }) {
  const color = EVENT_COLORS[event.type] ?? 'text-muted-foreground';

  return (
    <details className="border-b last:border-b-0">
      <summary className="flex cursor-pointer items-center gap-2 px-2 py-1 hover:bg-muted/50">
        <span className="text-muted-foreground w-6 shrink-0 text-right">{index}</span>
        <span className={cn('w-20 shrink-0 font-semibold', color)}>{event.type}</span>
        <span className="text-muted-foreground min-w-0 flex-1 truncate">{truncateJson(event)}</span>
      </summary>
      <pre className="bg-muted/30 overflow-x-auto border-t px-2 py-1 whitespace-pre-wrap">
        {JSON.stringify(event, null, 2)}
      </pre>
    </details>
  );
}

// ── Event Log ───────────────────────────────────────────────────

export function EventLog({ events, onClear }: EventLogProps) {
  const { containerRef, handleScroll } = useAutoScroll([events.length]);

  return (
    <div className="flex h-full flex-col font-mono text-xs">
      <div className="flex items-center justify-between border-b px-2 py-1">
        <span className="text-muted-foreground text-xs font-semibold">
          Events ({events.length})
        </span>
        <Button variant="outline" size="xs" onClick={onClear} disabled={events.length === 0}>
          Clear
        </Button>
      </div>
      <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
        {events.length === 0 ? (
          <div className="text-muted-foreground p-2">No events yet</div>
        ) : (
          events.map((event, i) => <EventRow key={i} event={event} index={i} />)
        )}
      </div>
    </div>
  );
}
